import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { NewSiteSeo, breadcrumb, clip } from "@/components/new-site/seo";
import "@/components/new-site/solution/mobile-solution.css";
import { NsButton } from "@/components/new-site/NewSiteChrome";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

import heroPhoto from "@/assets/new-site/sectors-hero-case.jpg.asset.json";
import resiliencePhoto from "@/assets/new-site/sectors-resilience.jpg.asset.json";
import streetworksPhoto from "@/assets/new-site/sectors-streetworks-highway-cropped.jpg.asset.json";
import constructionPhoto from "@/assets/new-site/sectors-construction-jcb.jpg.asset.json";
import hvmPhoto from "@/assets/new-site/sectors-hvm-bus-gate.jpg.asset.json";

/**
 * Figma "snapshot-live-sectors" (58:2330) — /new-site/sectors.
 * Photos in Figma are still placeholders/wireframes; stand-in published assets are
 * used until the real exports are supplied.
 */

// Bullet with "Label:" bold and the rest of the sentence regular weight.
function SectorBullet({ text }: { text: string }) {
  const idx = text.indexOf(": ");
  if (idx === -1) return <p>• {text}</p>;
  return (
    <p>
      • <span className="font-medium">{text.slice(0, idx + 1)}</span>{" "}
      <span className="font-normal">{text.slice(idx + 2)}</span>
    </p>
  );
}

const sectors = [
  {
    title: "Flood and Environment",
    slug: "flood-monitoring",
    seoTitle: "Flood & Culvert Monitoring Cameras | Snapshot Live",
    body: "Monitor water courses, remote drainage basins, trash screens and culverts. Snapshot Live delivers dependable visual checks without reliance on fixed infrastructure. Improve severe weather preparation with the visual information required to perform proactive maintenance of vital assets.",
    challenge: "Challenge: Reliable and up to date information on hard to reach flood prone assets",
    outcome: "Outcome: Before, During and After visuals and alerts to support proactive intervention",
    image: resiliencePhoto,
    alt: "Snapshot Live camera view of a flooded roadside with a railed drainage culvert and a 20 mph sign",
    caseStudy: { name: "National Highways", slug: "national-highways", highlight: "Time-stamped imagery of high-risk structures helps National Highways cut inspections by 66% and clear assets before flooding develops." },
  },
  {
    title: "Hostile Vehicle Mitigation",
    slug: "hostile-vehicle-mitigation",
    seoTitle: "Hostile Vehicle Mitigation Visual Assurance | Snapshot Live",
    body: "Strengthen assurance around temporary Hostile Vehicle Mitigation (HVM) deployments at sports venues and events. Working with CrowdGuard, Snapshot Live captures scheduled, time-stamped images during installation, at handover and throughout an event. AI-assisted review can flag potential movement, absence or misalignment for a targeted check, while competent personnel remain responsible for installation, inspection and sign-off.",
    challenge: "Challenge: Challenging dynamic environment, tight installation windows, and high risk if incorrect deployment",
    outcome: "Outcome: Time-stamped evidence, corrective action and post-event review",
    image: hvmPhoto,
    alt: "Red and white hostile vehicle mitigation barriers lined across a street bus gate, with two workers in high-visibility orange clothing",
    caseStudy: { name: "CrowdGuard", slug: "crowdguard", highlight: "Time-stamped audit trails and automated alerts for barrier alignment during high-risk public events." },
  },
  {
    title: "Streetworks and Traffic Management",
    slug: "streetworks",
    seoTitle: "Streetworks & Traffic Management Cameras | Snapshot Live",
    body: "Maintain full compliance and logistical visibility over active roadworks, highway closures, and civil repairs. Track temporary traffic management installations and minimize disruption safely.",
    challenge: "Challenge: Traditional site inspections are costly and infrequent, allowing non-compliant conditions to go undetected for extended periods and potentially putting road users at risk.",
    outcome: "Outcome: Safer, more compliant sites at lower cost and with less environmental impact, through proactive monitoring and fewer unnecessary site visits.",
    image: streetworksPhoto,
    alt: "Snapshot Live camera view of a highway lane closed with traffic cones and a 50 mph sign",
    caseStudy: { name: "Kent County Council", slug: "kent-county-council", highlight: "Remote verification of site presence, reducing permit overruns and physical inspection journeys." },
  },
  {
    title: "Sites and Facilities",
    slug: "sites-and-facilities",
    seoTitle: "Sites and Facilities Cameras, Solar & 4G | Snapshot Live",
    body: "From construction sites to remote and unmanned facilities, Snapshot Live provides autonomous visual intelligence to monitor conditions, improve safety and maintain operational oversight without the need for frequent site visits.",
    challenge: "Challenge: Infrequent, costly inspections leave safety hazards and operational issues undetected.",
    outcome: "Outcome: Safer, better-managed sites at lower cost and with less environmental impact.",
    image: constructionPhoto,
    alt: "Snapshot Live camera view of a construction yard with a JCB telehandler, pallets and skips",
    caseStudy: { name: "Kori Construction", slug: "kori-construction", highlight: "Monitoring of safety compliance and community impact to improve overall project performance" },
  },
];

function SectorCaseStudy({ caseStudy, compact = false }: { caseStudy: (typeof sectors)[number]["caseStudy"]; compact?: boolean }) {
  return (
    <aside aria-label={`${caseStudy.name} case study`} className={`flex flex-col rounded-[8px] border border-ns-plum/10 bg-ns-beige/50 ${compact ? "gap-2 px-4 py-3" : "gap-3 p-4"}`}>
      <p className="text-[12px] font-medium uppercase leading-[1.5] tracking-normal">Case Study: {caseStudy.name}</p>
      <p className="text-[14px] font-normal leading-[1.5] text-ns-plum/75">{caseStudy.highlight}</p>
      <Link to={`/case-studies/${caseStudy.slug}`} aria-label={`Read full ${caseStudy.name} case study`} className={`group inline-flex ${compact ? "min-h-8" : "min-h-11"} w-fit items-center gap-2 text-[14px] font-medium text-ns-plum underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ns-plum`}>
        Read full case study <ArrowRight aria-hidden="true" className="h-4 w-4 transition-transform group-hover:translate-x-1 motion-reduce:transition-none" />
      </Link>
    </aside>
  );
}

export default function NewSiteSectorsContent({ onContact }: { onContact: () => void }) {
  const navigate = useNavigate();
  // Pinned scroll sequence: sectors stay fixed on screen and swap as the user scrolls
  // (same pattern as the home page's product-feature sequence).
  const [activeSector, setActiveSector] = useState(0);
  const [openSector, setOpenSector] = useState<string | null>(null);
  const [mobileSector, setMobileSector] = useState(sectors[0].slug);
  const [reducedMotion, setReducedMotion] = useState(false);
  const sectorsSectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setReducedMotion(preference.matches);
    updatePreference();
    preference.addEventListener("change", updatePreference);
    return () => preference.removeEventListener("change", updatePreference);
  }, []);

  useEffect(() => {
    let animationFrame = 0;

    const updateActiveSector = () => {
      const section = sectorsSectionRef.current;
      if (!section) return;

      const bounds = section.getBoundingClientRect();
      const scrollableDistance = Math.max(section.offsetHeight - window.innerHeight, 1);
      const progress = Math.min(Math.max(-bounds.top / scrollableDistance, 0), 1);
      const nextSector = Math.min(Math.round(progress * (sectors.length - 1)), sectors.length - 1);
      setActiveSector((current) => (current === nextSector ? current : nextSector));
    };

    const onScroll = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(updateActiveSector);
    };

    updateActiveSector();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // Each sector has its own address (/sectors/<slug>); open the page at that sector.
  const { sector: sectorSlug } = useParams();
  // Old addresses keep working.
  const slugAliases: Record<string, string> = { construction: "sites-and-facilities" };
  const resolvedSlug = sectorSlug ? slugAliases[sectorSlug] ?? sectorSlug : sectorSlug;
  const linkedIndex = sectors.findIndex((s) => s.slug === resolvedSlug);
  const linked = linkedIndex >= 0 ? sectors[linkedIndex] : undefined;

  useEffect(() => {
    if (linkedIndex < 0) return;
    setMobileSector(sectors[linkedIndex].slug);
    const timer = window.setTimeout(() => {
      const mobile = document.getElementById(`sector-${sectors[linkedIndex].slug}`);
      const section = sectorsSectionRef.current;
      if (mobile && mobile.offsetParent !== null) {
        mobile.scrollIntoView({ block: "start" });
      } else if (section) {
        const distance = Math.max(section.offsetHeight - window.innerHeight, 1);
        const top = section.getBoundingClientRect().top + window.scrollY + (distance * linkedIndex) / (sectors.length - 1);
        window.scrollTo({ top });
      }
    }, 50);
    return () => window.clearTimeout(timer);
  }, [linkedIndex]);

  const currentSector = sectors[activeSector];

  return (

    <div className="ns-sectors font-archivo text-ns-plum">
      {linked ? (
        <NewSiteSeo
          path={`/sectors/${linked.slug}`}
          title={linked.seoTitle}
          description={clip(linked.body)}
          schema={[breadcrumb([["Home", "/"], ["Sectors", "/sectors"], [linked.title, `/sectors/${linked.slug}`]])]}
        />
      ) : (
        <NewSiteSeo
          path="/sectors"
           title="Flood, HVM, Traffic Management & Site Cameras | Snapshot Live"
           description="Autonomous cameras for flood monitoring, Hostile Vehicle Mitigation assurance, streetworks and traffic management, sites and facilities. Time-stamped imagery for better-informed decisions."
          schema={[breadcrumb([["Home", "/"], ["Sectors", "/sectors"]])]}
        />
      )}
      {/* Hero — Figma 58:2332: text left, full-height photo right with bottom scrim */}
      <section className="grid grid-cols-1 md:grid-cols-2 bg-ns-white">
        <div className="flex flex-col justify-center gap-6 px-6 py-14 md:pl-[94px] md:pr-12 md:py-0">
          <h1 className="text-[26px] font-medium leading-[1.15] tracking-[-0.8px] md:text-[40px] md:tracking-[-1.5px]">
             <span className="block mb-6 text-[12px] font-medium leading-[19.1px] tracking-normal text-ns-plum/50">Sectors</span>
            Built for demanding environments.
          </h1>
          <p className="max-w-[532px] text-[18px] leading-[1.5] text-ns-plum/50">
            Snapshot Live supports key sectors with robust, zero-mains autonomous cameras. We provide
            actionable operational data while reducing physical and carbon footprints.
          </p>
          <p className="max-w-[532px] text-[18px] leading-[1.5] text-ns-plum/50">
            Designed to support blind spot monitoring across many applications including the below:
          </p>
        </div>
        <div className="relative h-[390px] md:h-[701px] overflow-hidden bg-ns-beige">
          <img
            src={heroPhoto.url}
            alt="Field technician in hi-vis carrying a Snapshot Live case across site"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div
            aria-hidden
            className="absolute inset-x-0 bottom-0 h-[220px] bg-gradient-to-t from-black/50 to-transparent"
          />
        </div>
      </section>

      {/* Sector features — Figma 58:2342 / 58:2360 / 58:2370. Pinned scroll sequence:
          the section is taller than the viewport and holds one sector fixed while
          scrolling swaps it for the next, like the home page's product features. */}
      {/* Phones and tablets: directly selectable sectors, with details in the existing dialog. */}
      <section aria-label="Sectors" className="ns-mobile-sector-tabs bg-ns-white px-6 py-14 md:px-10 md:py-20 lg:hidden">
        <Tabs value={mobileSector} onValueChange={setMobileSector}>
          <TabsList aria-label="Choose sector" className="mb-6 grid h-auto w-full grid-cols-2 gap-2 md:mx-auto md:max-w-[720px] md:grid-cols-4 rounded-none bg-transparent p-0 text-ns-plum">
            {sectors.map((s, index) => (
              <TabsTrigger key={s.slug} value={s.slug} className="min-h-11 whitespace-normal rounded-full border border-ns-plum/20 px-3 py-2 text-[13px] text-ns-plum shadow-none data-[state=active]:border-ns-plum data-[state=active]:bg-ns-lime data-[state=active]:text-ns-plum data-[state=active]:shadow-none">
                {["Flood & Environment", "HVM", "Traffic Management", "Sites & Facilities"][index]}
              </TabsTrigger>
            ))}
          </TabsList>
          {sectors.map((s) => (
            <TabsContent key={s.slug} value={s.slug} className="mt-0">
            <article id={`sector-${s.slug}`} className="flex flex-col gap-6 md:mx-auto md:max-w-[720px]">
              <div className="relative h-[192px] md:h-[360px] overflow-hidden rounded-[8px] bg-ns-beige">
                <img src={s.image.url} alt={s.alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
              </div>
              <div className="flex min-w-0 flex-col gap-5">
                <h2 className="text-[24px] font-medium leading-[1.3] tracking-[-0.48px]">{s.title}</h2>
                <p className="text-[15px] font-medium leading-[1.5] text-ns-plum/50">{s.body}</p>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                  <NsButton variant="outline" onClick={() => setOpenSector(s.slug)}>Learn More</NsButton>
                  <Link to={`/case-studies/${s.caseStudy.slug}`} aria-label={`Read ${s.caseStudy.name} case study`} className="inline-flex min-h-11 items-center gap-2 text-[14px] font-medium text-ns-plum underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ns-plum">
                    Read Case Study <ArrowRight aria-hidden="true" className="h-4 w-4" />
                  </Link>
                </div>
                <Dialog open={openSector === s.slug} onOpenChange={(o) => setOpenSector(o ? s.slug : null)}>
                  <DialogContent className="max-h-[90vh] w-[calc(100%-32px)] md:max-w-xl overflow-y-auto rounded-[8px] bg-ns-white p-0 font-archivo text-ns-plum">
                    <div className="relative h-[192px] md:h-[300px] overflow-hidden rounded-t-[8px] bg-ns-beige">
                      <img src={s.image.url} alt={s.alt} className="absolute inset-0 h-full w-full object-cover" />
                    </div>
                    <div className="flex flex-col gap-5 px-6 pb-8">
                      <DialogTitle className="text-[24px] font-medium leading-[1.3] tracking-[-0.48px]">{s.title}</DialogTitle>
                      <DialogDescription className="sr-only">{s.body}</DialogDescription>
                      <div className="flex flex-col gap-3 text-[15px] font-medium leading-[1.5]">
                        <SectorBullet text={s.challenge} />
                        <SectorBullet text={s.outcome} />
                      </div>
                      <SectorCaseStudy caseStudy={s.caseStudy} />
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </article>
            </TabsContent>
          ))}
        </Tabs>
      </section>

      <section
        ref={sectorsSectionRef}
        className="relative hidden h-[240vh] bg-ns-white lg:block"
        aria-label="Sectors"
      >
        <div className="sticky top-0 flex min-h-screen flex-col justify-center gap-8 overflow-hidden px-6 py-10 md:gap-16 md:px-10 lg:px-[72px] md:py-[72px]">
          <div className="relative mx-auto w-full max-w-[1296px]">
            <article
              key={currentSector.slug}
              className="grid items-center gap-8 animate-in fade-in slide-in-from-bottom-12 duration-500 motion-reduce:animate-none md:grid-cols-2 md:gap-10 lg:grid-cols-[563px_1fr] lg:gap-[80px]"
            >
              <div className="relative h-[192px] md:h-[300px] lg:h-[348px] overflow-hidden rounded-[8px] bg-ns-beige lg:w-[563px] md:max-w-full">
                <img
                  src={currentSector.image.url}
                  alt={currentSector.alt}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
              <div className="flex min-w-0 flex-col gap-5">
                <h2 className="text-[24px] font-medium leading-[1.3] tracking-[-0.48px] md:text-[27px] md:tracking-[-0.54px]">
                  {currentSector.title}
                </h2>
                <p className="text-[15px] font-medium leading-[1.5] text-ns-plum/50">{currentSector.body}</p>
                <div className="flex flex-col gap-3 text-[15px] font-medium leading-[1.5]">
                  <SectorBullet text={currentSector.challenge} />
                  <SectorBullet text={currentSector.outcome} />
                </div>
                <SectorCaseStudy caseStudy={currentSector.caseStudy} compact />
              </div>
            </article>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2" aria-label={`Sector ${activeSector + 1} of ${sectors.length}`}>
              {sectors.map((s, index) => (
                <button
                  key={s.slug}
                  type="button"
                  aria-current={index === activeSector ? "true" : undefined}
                  onClick={() => {
                    const section = sectorsSectionRef.current;
                    if (!section) return;
                    const scrollable = Math.max(section.offsetHeight - window.innerHeight, 1);
                    const top = section.getBoundingClientRect().top + window.scrollY;
                    window.scrollTo({ top: top + (index / (sectors.length - 1)) * scrollable + 2, behavior: reducedMotion ? "auto" : "smooth" });
                  }}
                  className={`group flex flex-col items-start gap-2 font-archivo text-[13px] font-medium transition-colors ${index === activeSector ? "text-ns-plum" : "text-ns-plum/45 hover:text-ns-plum/75"}`}
                >
                  <span aria-hidden="true" className={`h-1 rounded-full transition-[width,background-color] duration-300 motion-reduce:transition-none ${index === activeSector ? "w-10 bg-ns-plum" : "w-4 bg-ns-plum/20 group-hover:bg-ns-plum/40"}`} />
                  {["Flood & Environment", "HVM", "Traffic Management", "Sites & Facilities"][index]}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* Conversion — Figma 58:2352 */}
      <section className="flex flex-col items-center gap-8 bg-gradient-to-r from-ns-orange via-ns-orange/80 to-ns-lime px-6 py-14 text-center md:px-[72px] md:py-[120px]">
        <div className="flex w-full max-w-[720px] flex-col items-center gap-4 text-ns-white">
          <h2 className="text-[26px] font-medium leading-[1.15] tracking-[-0.8px] md:text-[40px] md:tracking-[-1.5px]">
            Smarter visibility for your sector
          </h2>
          <p className="w-full max-w-[564px] text-[15px] font-medium leading-[1.5]">
            Speak with our sector specialists to discover how we customise configurations for resilience,
             Hostile Vehicle Mitigation, transport, and civil engineering teams.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <NsButton variant="lime" onClick={onContact} className="md:hidden">Contact Us</NsButton>
          <NsButton variant="white" onClick={onContact} className="hidden md:inline-flex">Contact Us</NsButton>
          <NsButton variant="light" onClick={() => navigate("/case-studies")}>Case Studies</NsButton>
        </div>
      </section>
    </div>
  );
}
